import { useState, useEffect, useCallback } from 'react';
import { apiClient, useAuth } from '../../contexts/AuthContext';
import { Trophy, TrendingUp, TrendingDown, Minus, Clock, Target, ChevronRight, Award, ChevronDown, ChevronUp, Medal, BookOpen, Flame, AlertTriangle, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Spinner } from './ChallengeShared';
import { UserAvatar } from '../UserAvatar';
import { PodiumTab } from './ChallengePodiumTab';
import { ReglesTab } from './ChallengeReglesTab';
import { EngagementBadge } from './EngagementBadge';
import { MaCautionCard } from './MaCautionCard';

function TrendArrow({ direction, pct }) {
  if (!direction || direction === 'stable') return <Minus className="w-3.5 h-3.5 text-zinc-500" />;
  if (direction === 'down') return <span className="flex items-center gap-0.5 text-[#2e7d32]"><TrendingDown className="w-3.5 h-3.5" /><span className="text-[10px] font-mono font-bold">{Math.abs(pct)}%</span></span>;
  return <span className="flex items-center gap-0.5 text-[#c0392b]"><TrendingUp className="w-3.5 h-3.5" /><span className="text-[10px] font-mono font-bold">+{pct}%</span></span>;
}

function OfficialBadge({ date }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#FF6B35]/15 border border-[#FF6B35]/30 rounded text-[9px] text-[#FF6B35] font-bold">
      <Award className="w-2.5 h-2.5" /> Officiel {date ? `(${date})` : ''}
    </span>
  );
}

// ─── Section 1: Ma Position ─────────────────────────────────────────────────
function MyPositionCard({ data, error, onRetry, onOpenFiche }) {
  if (error) {
    return (
      <div className="bg-[#1B1B1B] border border-[#c0392b]/40 rounded-xl p-6 text-center" data-testid="position-error">
        <p className="text-sm text-zinc-200 font-bold">Connexion instable</p>
        <p className="text-xs text-zinc-500 mt-1">Impossible de charger tes donnees Challenge pour le moment. Ce n'est pas un probleme d'inscription — reessaie dans un instant.</p>
        <button onClick={onRetry} data-testid="position-retry-btn" className="mt-3 px-4 py-2 bg-[#FF6B35] text-white text-xs font-bold rounded-lg active:scale-95 transition-transform">Reessayer</button>
      </div>
    );
  }
  if (!data?.enrolled) {
    return (
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-xl p-6 text-center" data-testid="not-enrolled">
        <p className="text-sm text-zinc-400">Tu n'es pas encore inscrit au Challenge.</p>
        <p className="text-xs text-zinc-600 mt-1">Contacte le coach pour t'inscrire.</p>
      </div>
    );
  }
  if (!data.actif) {
    return (
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-xl p-5 text-center" data-testid="not-active">
        <p className="text-sm text-zinc-400">Ton profil Challenge n'est pas encore actif.</p>
        <p className="text-xs text-zinc-600 mt-1">Le coach doit valider ton inscription.</p>
      </div>
    );
  }

  if (data.baseline_status === 'en_attente_pesee') {
    return (
      <div className="bg-[#1B1B1B] border border-[#FFB800]/30 rounded-xl p-5 text-center" data-testid="awaiting-baseline">
        <Clock className="w-8 h-8 text-[#FFB800] mx-auto mb-2" />
        <p className="text-sm text-[#FFB800] font-bold">En attente de pesee initiale</p>
        <p className="text-xs text-zinc-500 mt-1">Ta premiere mesure officielle servira de point de depart (baseline J0).</p>
      </div>
    );
  }

  // Caution alert: compute how close to losing it
  const cautionAlert = _computeCautionAlert(data);

  return (
    <div className="bg-gradient-to-br from-[#1B1B1B] to-[#1B1B1B]/80 border border-[#FF6B35]/25 rounded-xl overflow-hidden" data-testid="my-position-card">
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <UserAvatar name={data.nom} photoPath={data.profile_photo} size="md" isChallenger={data.is_challenger} interactive={false} />
            {data.rank && (
              <span className="absolute -top-1 -right-1 w-6 h-6 bg-[#FF6B35] rounded-full flex items-center justify-center text-[10px] font-black text-white">
                #{data.rank}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-lg font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{data.nom}</p>
            <p className="text-[10px] text-zinc-500">
              <span className={`font-bold ${data.is_challenger ? 'text-[#FF6B35]' : 'text-zinc-500'}`}>
                {data.is_challenger ? 'Challenger' : 'Adherent'}
              </span>
              {' · '}{data.objectif === 'gras' ? 'Perte gras' : 'Prise muscle'} · {data.total_active} challengers
            </p>
          </div>
          <div className="text-right">
            <p className="font-mono text-2xl font-black text-[#FF6B35]">{(data.score ?? 0).toFixed(1)}</p>
            <p className="text-[9px] text-zinc-500">Score officiel</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-[#0F0F0F] rounded-lg p-2.5 text-center">
            <p className="text-[9px] text-zinc-500 mb-0.5">Score Gras</p>
            <p className="font-mono text-sm font-bold text-white">{(data.score_mg ?? 0).toFixed(1)}</p>
          </div>
          <div className="bg-[#0F0F0F] rounded-lg p-2.5 text-center">
            <p className="text-[9px] text-zinc-500 mb-0.5">Score Muscle</p>
            <p className="font-mono text-sm font-bold text-white">{(data.score_mm ?? 0).toFixed(1)}</p>
          </div>
        </div>

        {/* Caution Alert */}
        {cautionAlert && (
          <div className={`mt-3 rounded-lg p-3 flex items-center gap-3 ${cautionAlert.level === 'danger' ? 'bg-red-500/15 border border-red-500/30' : cautionAlert.level === 'warning' ? 'bg-[#FFB800]/15 border border-[#FFB800]/30' : 'bg-green-500/10 border border-green-500/30'}`} data-testid="caution-alert">
            <span className="text-lg">{cautionAlert.level === 'danger' ? '🚨' : cautionAlert.level === 'warning' ? '⚠️' : '✅'}</span>
            <div className="flex-1">
              <p className={`text-xs font-bold ${cautionAlert.level === 'danger' ? 'text-red-400' : cautionAlert.level === 'warning' ? 'text-[#FFB800]' : 'text-green-400'}`}>
                {cautionAlert.title}
              </p>
              <p className={`text-[10px] mt-0.5 ${cautionAlert.level === 'danger' ? 'text-red-400/70' : cautionAlert.level === 'warning' ? 'text-[#FFB800]/70' : 'text-green-400/70'}`}>
                {cautionAlert.message}
              </p>
            </div>
          </div>
        )}

        {data.trend && (
          <div className="mt-3 bg-[#0F0F0F] rounded-lg p-2.5 flex items-center justify-between">
            <span className="text-[10px] text-zinc-500">Tendance declaree</span>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-white">{data.trend.poids}kg</span>
              <TrendArrow direction={data.trend.direction} pct={data.trend.pct_change} />
              <span className="text-[9px] text-zinc-600">{data.trend.date}</span>
            </div>
          </div>
        )}

        {data.last_official && (
          <div className="mt-2 flex items-center gap-2">
            <OfficialBadge date={data.last_official_date} />
            <span className="font-mono text-[10px] text-zinc-400">
              {data.last_official.poids}kg · {data.last_official.mg}% · {data.last_official.mm}kg
            </span>
          </div>
        )}

        {!data.is_challenger && data.ghost_rank && (
          <div className="mt-3 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-lg p-2.5 text-center">
            <p className="text-xs text-[#FF6B35] font-bold">
              Avec la caution, tu serais <span className="font-mono text-base">#{data.ghost_rank}</span> du classement !
            </p>
            <p className="text-[9px] text-zinc-500 mt-0.5">Contacte le coach pour passer Challenger</p>
          </div>
        )}

        {data.pid && onOpenFiche && (
          <button onClick={onOpenFiche} data-testid="view-my-fiche-btn"
            className="mt-3 w-full py-2.5 rounded-lg bg-[#FF6B35]/10 border border-[#FF6B35]/30 text-[#FF6B35] text-xs font-bold hover:bg-[#FF6B35]/20 transition-colors">
            📊 Ma fiche complète — mesures & objectifs par palier
          </button>
        )}
      </div>
    </div>
  );
}

function _computeCautionAlert() { return null; }

// ─── Onglet Cautions: qui a sauvé ou perdu sa caution ───────────────────────
function CautionsStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/challenge/cautions')
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Erreur chargement cautions'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!data) return null;

  const withPaliers = data.participants.filter(p => (p.paliers || []).length > 0);
  const lost = withPaliers.filter(p => p.paliers.some(pal => pal.status === 'rouge'));
  const saved = withPaliers.filter(p => !p.paliers.some(pal => pal.status === 'rouge') && p.paliers.some(pal => pal.status === 'vert'));
  const pending = data.participants.filter(p => !lost.includes(p) && !saved.includes(p));

  const STATUS_DOT = { vert: 'bg-[#2e7d32]', rouge: 'bg-[#c0392b]', gris: 'bg-zinc-700' };
  const Row = ({ p }) => {
    const [open, setOpen] = useState(false);
    const savedAmt = p.paliers.filter(x => x.status === 'vert').reduce((s, x) => s + x.montant, 0);
    const lostAmt = p.paliers.filter(x => x.status === 'rouge').reduce((s, x) => s + x.montant, 0);
    const evaluated = p.paliers.filter(x => x.label !== 'P1' && x.detail && x.status !== 'gris');
    const j0d = p.paliers.find(x => x.label === 'P1')?.detail;
    return (
      <div className="rounded-lg bg-[#1B1B1B] border border-[#2A2A2E]" data-testid={`caution-row-${p.nom}`}>
        <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 p-2.5 text-left">
          <UserAvatar name={p.nom} size="xs" isChallenger interactive={false} />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-white truncate">{p.nom}</p>
            <div className="flex items-center gap-1.5 mt-1">
              {p.paliers.map(pal => (
                <span key={pal.label} className="flex items-center gap-0.5">
                  <span className={`w-2 h-2 rounded-full ${STATUS_DOT[pal.status] || 'bg-zinc-700'}`} />
                  <span className="text-[8px] text-zinc-500 font-mono">{pal.label}</span>
                </span>
              ))}
            </div>
          </div>
          <div className="text-right font-mono text-[10px]">
            {savedAmt > 0 && <p className="text-[#2e7d32] font-bold">+{savedAmt.toLocaleString()} XPF</p>}
            {lostAmt > 0 && <p className="text-[#c0392b] font-bold">-{lostAmt.toLocaleString()} XPF</p>}
            {savedAmt === 0 && lostAmt === 0 && <p className="text-zinc-600">En attente</p>}
          </div>
          {(evaluated.length > 0 || j0d) && (open ? <ChevronUp className="w-3.5 h-3.5 text-zinc-600" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-600" />)}
        </button>
        {open && (evaluated.length > 0 || j0d) && (
          <div className="px-2.5 pb-2.5 space-y-1.5 border-t border-[#2A2A2E] pt-2">
            {j0d && j0d.poids_reel != null && (
              <div className="text-[9px] font-mono bg-[#0F0F0F] rounded p-2">
                <p className="font-bold mb-1 text-zinc-400">P1 — Pesée J0{j0d.date_mesure ? ` (${j0d.date_mesure})` : ''}</p>
                <p className="text-zinc-500">⚖️ {j0d.poids_reel} kg{j0d.mg_reel != null ? ` · 🔥 ${j0d.mg_reel}%` : ''}{j0d.muscle_reel != null ? ` · 💪 ${j0d.muscle_reel} kg` : ''}</p>
              </div>
            )}
            {evaluated.map(pal => {
              const d = pal.detail;
              const okPoids = d.poids_reel != null && d.poids_reel <= d.poids_cible;
              const okGras = d.mg_reel != null && d.mg_cible != null && d.mg_reel <= d.mg_cible;
              const okMuscle = d.muscle_reel != null && d.muscle_reel >= d.muscle_cible;
              return (
                <div key={pal.label} className="text-[9px] font-mono bg-[#0F0F0F] rounded p-2">
                  <p className={`font-bold mb-1 ${pal.status === 'vert' ? 'text-[#2e7d32]' : 'text-[#c0392b]'}`}>
                    {pal.label} — {pal.status === 'vert' ? 'Caution sécurisée ✓' : 'Caution perdue ✗'}{d.date_mesure ? ` (pesée ${d.date_mesure})` : ' (pas de pesée)'}
                  </p>
                  <p className={okPoids ? 'text-[#2e7d32]' : 'text-zinc-500'}>
                    ⚖️ Poids : {d.poids_reel ?? '?'}kg {okPoids ? '≤' : '>'} cible {d.poids_cible}kg {okPoids ? '✓' : '✗'}
                  </p>
                  {d.mg_cible != null && (
                    <p className={okGras ? 'text-[#2e7d32]' : 'text-zinc-500'}>
                      🔥 Gras : {d.mg_reel ?? '?'}% {okGras ? '≤' : '>'} cible {d.mg_cible}% {okGras ? '✓' : '✗'}
                    </p>
                  )}
                  <p className={okMuscle ? 'text-[#2e7d32]' : 'text-zinc-500'}>
                    💪 Muscle : {d.muscle_reel ?? '?'}kg {okMuscle ? '≥' : '<'} cible {d.muscle_cible}kg {okMuscle ? '✓' : '✗'}
                  </p>
                  {pal.status === 'rouge' && <p className="text-[#c0392b] mt-1">Aucune des 3 routes atteinte → {pal.montant.toLocaleString()} XPF perdus (cagnotte lots)</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="cautions-status-tab">
      {/* Explication de la règle */}
      <details className="bg-[#1B1B1B] border border-[#FFB800]/30 rounded-lg" data-testid="caution-rules-info">
        <summary className="p-3 text-[11px] font-bold text-[#FFB800] cursor-pointer flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" /> Comment sauver (ou perdre) sa caution ?
        </summary>
        <div className="px-3 pb-3 text-[10px] text-zinc-400 space-y-1.5 leading-relaxed">
          <p>Ta caution est jouée en <span className="text-white font-bold">4 parts</span> : <span className="font-mono">P1</span> (validée dès le paiement), puis <span className="font-mono">S4, S8, S12</span>.</p>
          <p>À chaque palier, tu dois atteindre <span className="text-white font-bold">au moins UNE des 3 routes</span>, calculées depuis ton J0 au prorata (S4 = 33%, S8 = 66%, S12 = 100%) :</p>
          <p className="pl-2">⚖️ <span className="text-white">Poids</span> −{data.routes_pct?.poids ?? 4}% <span className="text-zinc-500">OU</span> 🔥 <span className="text-white">Gras</span> −{data.routes_pct?.gras ?? 4}% <span className="text-zinc-500">OU</span> 💪 <span className="text-white">Muscle</span> +{data.routes_pct?.muscle ?? 4}%</p>
          <p className="text-[#FFB800]">⚠️ Être bien classé (voire sur le podium) ne suffit pas : le classement compare aux autres, la caution compare à <span className="font-bold">TON objectif personnel</span>. On peut donc être sur le podium ET rater sa cible de palier.</p>
          <p>Palier raté = la part de caution part dans la <span className="text-white font-bold">cagnotte des lots</span> 🎁. Touche un nom pour voir le détail cible vs réalisé.</p>
        </div>
      </details>

      <div className="grid grid-cols-3 gap-2">
        <div className="bg-[#1B1B1B] border border-[#2e7d32]/40 rounded-lg p-2.5 text-center">
          <p className="text-[9px] text-zinc-500 uppercase font-bold">Sauvées</p>
          <p className="text-sm font-black text-[#2e7d32] font-mono">{(data.totaux?.vert ?? 0).toLocaleString()}</p>
          <p className="text-[8px] text-zinc-600">XPF</p>
        </div>
        <div className="bg-[#1B1B1B] border border-[#c0392b]/40 rounded-lg p-2.5 text-center">
          <p className="text-[9px] text-zinc-500 uppercase font-bold">Perdues</p>
          <p className="text-sm font-black text-[#c0392b] font-mono">{(data.totaux?.rouge ?? 0).toLocaleString()}</p>
          <p className="text-[8px] text-zinc-600">XPF → cagnotte lots</p>
        </div>
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-2.5 text-center">
          <p className="text-[9px] text-zinc-500 uppercase font-bold">En jeu</p>
          <p className="text-sm font-black text-zinc-400 font-mono">{(data.totaux?.gris ?? 0).toLocaleString()}</p>
          <p className="text-[8px] text-zinc-600">XPF</p>
        </div>
      </div>

      {saved.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-[#2e7d32] uppercase mb-1.5 flex items-center gap-1"><Shield className="w-3 h-3" /> Caution sauvée ({saved.length})</p>
          <div className="space-y-1.5">{saved.map(p => <Row key={p.id} p={p} />)}</div>
        </div>
      )}
      {lost.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-[#c0392b] uppercase mb-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Caution perdue ({lost.length})</p>
          <div className="space-y-1.5">{lost.map(p => <Row key={p.id} p={p} />)}</div>
        </div>
      )}
      {pending.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-zinc-500 uppercase mb-1.5 flex items-center gap-1"><Clock className="w-3 h-3" /> En attente ({pending.length})</p>
          <div className="space-y-1.5">{pending.map(p => <Row key={p.id} p={p} />)}</div>
        </div>
      )}
      <p className="text-[9px] text-zinc-600 text-center">🟢 palier validé · 🔴 objectif raté · ⚪ à venir</p>
    </div>
  );
}

// ─── Section 2: Classement avec 3 onglets ───────────────────────────────────
function Leaderboard({ leaderboard, onSelectChallenger }) {
  const [activeTab, setActiveTab] = useState('recomp');
  const [showMissing, setShowMissing] = useState(false);
  if (!leaderboard) return null;
  const { rankings, latest_palier_label, latest_palier_date, total_challengers } = leaderboard;

  const TABS = [
    { id: 'recomp', label: 'Recomp', scoreKey: 'score', color: '#FF6B35' },
    { id: 'poids', label: 'Poids', scoreKey: 'score_poids', color: '#a855f7' },
    { id: 'gras', label: 'Gras', scoreKey: 'score_mg', color: '#2e7d32' },
    { id: 'muscle', label: 'Muscle', scoreKey: 'score_mm', color: '#3b82f6' },
    { id: 'cautions', label: 'Cautions', scoreKey: 'score', color: '#FFB800' },
  ];

  const currentTab = TABS.find(t => t.id === activeTab);
  const scoreKey = currentTab.scoreKey;

  // Sort: participants with data first (by score desc), missing data at bottom
  const withData = rankings.filter(r => !r.missing_data);
  const missingData = rankings.filter(r => r.missing_data);
  const sortedWithData = [...withData].sort((a, b) => (b[scoreKey] ?? 0) - (a[scoreKey] ?? 0));
  const sortedMissing = [...missingData].sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
  const sorted = [...sortedWithData, ...sortedMissing];
  // Assign ranks (only for those with data)
  sortedWithData.forEach((r, i) => { r._rank = i + 1; });
  sortedMissing.forEach((r) => { r._rank = '-'; });

  const RANK_STYLES = {
    1: { badge: 'bg-gradient-to-r from-[#FFB800] to-[#FFA000]', text: 'text-black', medal: '👑', glow: 'shadow-[0_0_20px_rgba(255,184,0,0.3)]', border: 'border-[#FFB800]/50' },
    2: { badge: 'bg-gradient-to-r from-[#C0C0C0] to-[#A0A0A0]', text: 'text-black', medal: '🥈', glow: 'shadow-[0_0_15px_rgba(192,192,192,0.2)]', border: 'border-zinc-400/40' },
    3: { badge: 'bg-gradient-to-r from-[#CD7F32] to-[#B87333]', text: 'text-white', medal: '🥉', glow: 'shadow-[0_0_15px_rgba(205,127,50,0.2)]', border: 'border-amber-700/40' },
  };

  return (
    <div data-testid="leaderboard-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          <Trophy className="w-5 h-5 text-[#FFB800] inline mr-1.5" />Classement
        </h2>
        {latest_palier_label && <OfficialBadge date={`${latest_palier_label}${latest_palier_date ? ` - ${latest_palier_date}` : ''}`} />}
      </div>

      {/* 3 Tabs */}
      <div className="flex gap-1 mb-4 bg-[#0F0F0F] rounded-xl p-1" data-testid="ranking-tabs">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 px-2 rounded-lg text-[10px] font-bold transition-all ${
              activeTab === tab.id
                ? 'text-white shadow-md' : 'text-zinc-500 hover:text-zinc-300'
            }`}
            style={activeTab === tab.id ? { backgroundColor: tab.color + '25', color: tab.color, borderBottom: `2px solid ${tab.color}` } : {}}
            data-testid={`tab-${tab.id}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Onglet Cautions: sauvées / perdues */}
      {activeTab === 'cautions' ? <CautionsStatus /> : (<>

      {/* TOP 3 — Podium (only participants with data) */}
      {sortedWithData.length >= 3 && (
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="podium-top3">
          {[1, 0, 2].map((idx) => {
            const r = sortedWithData[idx];
            if (!r) return null;
            const style = RANK_STYLES[r._rank];
            const isFirst = r._rank === 1;
            return (
              <button key={r.pid} onClick={() => onSelectChallenger?.(r)}
                className={`relative flex flex-col items-center p-3 rounded-xl border ${style.border} bg-[#1B1B1B] ${style.glow} transition-transform hover:scale-105 ${isFirst ? 'pt-2 -mt-2' : ''}`}
                data-testid={`podium-${r._rank}`}>
                <span className="text-2xl mb-1">{style.medal}</span>
                <UserAvatar name={r.nom} photoPath={r.profile_photo} size="sm" isChallenger interactive={false} />
                <p className="text-[10px] font-bold text-white mt-1.5 truncate w-full text-center">{r.nom}</p>
                <div className={`mt-1 px-2 py-0.5 rounded-full text-[10px] font-black ${style.badge} ${style.text}`}>
                  {activeTab === 'poids' ? `-${(r.perte_kg ?? 0).toFixed(1)} kg` : (r[scoreKey] ?? 0).toFixed(1)}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Full ranking list — participants WITH data */}
      <p className="text-[9px] uppercase tracking-wider font-bold mb-2" style={{ color: currentTab.color }}>
        {currentTab.label} ({sortedWithData.length})
      </p>
      <div className="space-y-1.5">
        {sortedWithData.slice(sortedWithData.length >= 3 ? 3 : 0).map((r) => (
          <button key={r.pid} onClick={() => onSelectChallenger?.(r)}
            className="w-full flex items-center gap-2.5 p-2.5 bg-[#1B1B1B] border border-[#2A2A2E] rounded-xl hover:border-[#FF6B35]/30 transition-all text-left group"
            data-testid={`leaderboard-row-${r._rank}`}>
            <span className={`w-8 h-8 flex items-center justify-center rounded-xl text-[11px] font-black ${
              r._rank <= 10 ? 'text-[#FF6B35] bg-[#FF6B35]/15' : 'bg-[#0F0F0F] text-zinc-600'
            }`}>
              {r._rank}
            </span>
            <UserAvatar name={r.nom} photoPath={r.profile_photo} size="sm" isChallenger interactive={false} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate group-hover:text-[#FF6B35] transition-colors">{r.nom}</p>
              <p className="text-[9px] text-zinc-600">{r.score_type === 'delta' ? r.last_official_date : 'J0'}</p>
            </div>
            <div className="text-right">
              <p className="font-mono text-sm font-bold" style={{ color: r._rank <= 10 ? currentTab.color : '#71717a' }}>
                {activeTab === 'poids' ? `-${(r.perte_kg ?? 0).toFixed(1)} kg` : (r[scoreKey] ?? 0).toFixed(1)}
              </p>
              {activeTab === 'poids' && <p className="text-[9px] text-zinc-600 font-mono">{(r.score_poids ?? 0).toFixed(1)}%</p>}
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-zinc-700 group-hover:text-[#FF6B35] transition-colors" />
          </button>
        ))}
        {sortedWithData.length === 0 && (
          <div className="bg-[#1B1B1B] rounded-xl p-6 text-center">
            <p className="text-sm text-zinc-500">Aucun participant avec donnees.</p>
          </div>
        )}
      </div>

      {/* Collapsible section for MISSING DATA participants */}
      {sortedMissing.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowMissing(!showMissing)}
            className="w-full flex items-center justify-between p-2.5 bg-[#1B1B1B]/60 border border-[#2A2A2E]/50 rounded-xl text-left"
            data-testid="toggle-missing-data">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-[#c0392b]" />
              <span className="text-xs text-zinc-400 font-bold">Donnees manquantes ({sortedMissing.length})</span>
            </div>
            {showMissing ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
          </button>
          {showMissing && (
            <div className="space-y-1 mt-1.5">
              {sortedMissing.map((r) => (
                <button key={r.pid} onClick={() => onSelectChallenger?.(r)}
                  className="w-full flex items-center gap-2.5 p-2 bg-[#1B1B1B]/40 border border-[#2A2A2E]/30 rounded-lg opacity-60 text-left"
                  data-testid={`leaderboard-missing-${r.nom}`}>
                  <span className="w-6 h-6 flex items-center justify-center rounded-lg text-[10px] font-black bg-[#0F0F0F] text-zinc-700">-</span>
                  <UserAvatar name={r.nom} photoPath={r.profile_photo} size="xs" isChallenger interactive={false} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-500 font-medium truncate">{r.nom}</p>
                    {r.last_official ? (
                      <p className="text-[9px] text-zinc-600 font-mono truncate" data-testid={`missing-last-mesure-${r.nom}`}>
                        {r.last_official.poids}kg · {r.last_official.mg}% · {r.last_official.mm}kg{r.last_official_date ? ` (${r.last_official_date})` : ''}
                      </p>
                    ) : (
                      <p className="text-[9px] text-zinc-700 italic">Aucune mesure</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    {r.last_official && (
                      <span className="font-mono text-[10px] text-zinc-600">{(r[scoreKey] ?? 0).toFixed(1)}</span>
                    )}
                    <span className="text-[8px] font-bold text-[#c0392b] bg-[#c0392b]/10 px-1.5 py-0.5 rounded whitespace-nowrap">Donnée manquante</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      </>)}
    </div>
  );
}

// ─── Section 3: Pronostic du Podium ─────────────────────────────────────────
function PronosticSection({ bettingData, onRefresh }) {
  const [selectedCid, setSelectedCid] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  if (!bettingData) return null;
  const { challengers, my_bets, pot_confirmed, is_locked, lock_date, min_bet, is_settled } = bettingData;
  const myBetCids = my_bets.map(b => b.challenger_id);

  const handleBet = async () => {
    if (!selectedCid || !amount || parseInt(amount) < min_bet) {
      toast.error(`Choisis un challenger et un montant (min ${min_bet} XPF)`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.post('/challenge/podium-bet', { challenger_id: selectedCid, amount: parseInt(amount) });
      toast.success(data.message);
      setSelectedCid(''); setAmount('');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const countdown = getCountdown(lock_date);

  return (
    <div data-testid="pronostic-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          <Target className="w-5 h-5 text-[#FFB800] inline mr-1.5" />Pronostic du Podium
        </h2>
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-bold text-[#FFB800]">{(pot_confirmed / 1000).toFixed(0)}k XPF</span>
          {!is_locked && countdown && (
            <span className="text-[9px] bg-[#FFB800]/20 text-[#FFB800] px-2 py-0.5 rounded-full font-mono flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" />{countdown}
            </span>
          )}
          {is_locked && !is_settled && <span className="text-[9px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full font-bold">Clos</span>}
          {is_settled && <span className="text-[9px] bg-[#2e7d32]/20 text-[#2e7d32] px-2 py-0.5 rounded-full font-bold">Regle</span>}
        </div>
      </div>

      {/* My bets */}
      {my_bets.length > 0 && (
        <div className="mb-4 bg-[#1B1B1B] border border-[#FFB800]/20 rounded-lg p-3">
          <p className="text-xs text-[#FFB800] font-bold mb-2">Mes pronostics ({my_bets.length})</p>
          <div className="space-y-1.5">
            {my_bets.map(b => (
              <div key={b.id} className="flex items-center gap-2 p-2 bg-[#0F0F0F] rounded-lg">
                <span className="text-xs text-white font-medium flex-1">{b.challenger_name}</span>
                <span className="font-mono text-xs text-[#FFB800]">{b.amount?.toLocaleString()} XPF</span>
                <BetBadge status={b.status} payout={b.payout} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bet form */}
      {!is_locked && !is_settled && (
        <div className="mb-4 bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-3">
          <div className="flex gap-2">
            <select value={selectedCid} onChange={e => setSelectedCid(e.target.value)}
              className="flex-1 bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-lg px-2 py-2 text-xs" data-testid="bet-select">
              <option value="">Challenger...</option>
              {challengers.map(c => (
                <option key={c.pid} value={c.pid}>{c.nom} {myBetCids.includes(c.pid) ? '(deja)' : ''}</option>
              ))}
            </select>
            <Input type="number" min={min_bet} step={500} value={amount} onChange={e => setAmount(e.target.value)}
              placeholder={`${min_bet}`} className="w-24 bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-xs" data-testid="bet-amount" />
            <Button onClick={handleBet} disabled={saving || !selectedCid || !amount} size="sm"
              className="bg-[#FFB800] text-black font-bold text-xs px-3 hover:bg-[#FFD000]" data-testid="bet-submit">
              Miser
            </Button>
          </div>
        </div>
      )}

      {/* Challengers with bets — only show those who received bets */}
      {challengers.filter(c => c.bet_count > 0).length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[9px] uppercase tracking-wider font-bold text-[#FFB800] mb-1">Challengers avec paris</p>
          {challengers.filter(c => c.bet_count > 0).map(c => (
            <div key={c.pid} className="flex items-center gap-2.5 p-2.5 bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg" data-testid={`betting-challenger-${c.nom}`}>
              <UserAvatar name={c.nom} photoPath={c.profile_photo} size="sm" isChallenger interactive={false} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{c.nom}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {c.last_official && (
                    <span className="text-[9px] text-zinc-500 font-mono">
                      {c.last_official.poids}kg
                    </span>
                  )}
                  {c.last_official_date && <OfficialBadge date={c.last_official_date} />}
                </div>
              </div>
              <div className="text-right flex-shrink-0 flex items-center gap-2">
                <span className="text-[9px] font-bold text-[#FFB800] bg-[#FFB800]/15 px-1.5 py-0.5 rounded">{c.bet_count} pari{c.bet_count > 1 ? 's' : ''}</span>
                {c.trend ? (
                  <TrendArrow direction={c.trend.direction} pct={c.trend.pct} />
                ) : (
                  <span className="text-[9px] text-zinc-600">-</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BetBadge({ status, payout }) {
  const map = {
    pending_payment: { label: 'En attente', cls: 'bg-[#FFB800]/20 text-[#FFB800]' },
    confirmed: { label: 'Confirme', cls: 'bg-[#2e7d32]/20 text-[#2e7d32]' },
    won: { label: `Gagne ${payout?.toLocaleString() || ''} XPF`, cls: 'bg-[#FFB800]/20 text-[#FFB800]' },
    lost: { label: 'Perdu', cls: 'bg-zinc-800 text-zinc-500' },
  };
  const c = map[status] || map.pending_payment;
  return <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${c.cls}`}>{c.label}</span>;
}

function getCountdown(lockDateStr) {
  if (!lockDateStr) return null;
  try {
    const lock = new Date(lockDateStr.includes('+') || lockDateStr.includes('Z') ? lockDateStr : lockDateStr + '+00:00');
    const diff = lock - new Date();
    if (diff <= 0) return null;
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    return days > 0 ? `${days}j ${hours}h` : `${hours}h`;
  } catch { return null; }
}

// ─── Challenger Detail Modal ────────────────────────────────────────────────
function ChallengerDetail({ challenger, onClose }) {
  const [fiche, setFiche] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (challenger?.pid) {
      apiClient.get(`/challenge/participant-fiche/${challenger.pid}`)
        .then(r => setFiche(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [challenger?.pid]);

  if (!challenger) return null;
  const f = fiche;

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-end justify-center p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="bg-[#1B1B1B] rounded-t-2xl sm:rounded-xl w-full max-w-md max-h-[85vh] overflow-y-auto pb-safe mb-[60px] sm:mb-0" onClick={e => e.stopPropagation()} data-testid="challenger-detail">
        {/* Close handle bar */}
        <div className="sticky top-0 bg-[#1B1B1B] rounded-t-2xl pt-2 pb-1 flex justify-center z-10">
          <div className="w-10 h-1 rounded-full bg-zinc-600" />
        </div>
        <div className="px-5 pb-5 space-y-4">
          {/* Header */}
          <div className="flex items-center gap-3">
            <UserAvatar name={challenger.nom} photoPath={f?.profile_photo || challenger.profile_photo} size="md" isChallenger={f?.is_challenger} interactive={false} />
            <div>
              <p className="text-lg font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{challenger.nom}</p>
              <p className="text-xs text-zinc-500">
                <span className={f?.is_challenger ? 'text-[#FF6B35] font-bold' : 'text-zinc-500'}>{f?.is_challenger ? 'Challenger' : 'Adherent'}</span>
                {challenger.rank && ` · #${challenger.rank}`}
              </p>
            </div>
            <button onClick={onClose} className="ml-auto text-zinc-500 hover:text-white text-xl">&times;</button>
          </div>

          {loading ? (
            <div className="text-center py-6"><div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : f ? (
            <>
              {/* Scores */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-[#0F0F0F] rounded-lg p-2.5">
                  <p className="font-mono text-lg font-bold text-[#FF6B35]">{f.scores.total}</p>
                  <p className="text-[9px] text-zinc-500">Score total</p>
                </div>
                <div className="bg-[#0F0F0F] rounded-lg p-2.5">
                  <p className="font-mono text-lg font-bold text-white">{f.scores.mg}</p>
                  <p className="text-[9px] text-zinc-500">Score Gras</p>
                </div>
                <div className="bg-[#0F0F0F] rounded-lg p-2.5">
                  <p className="font-mono text-lg font-bold text-white">{f.scores.mm}</p>
                  <p className="text-[9px] text-zinc-500">Score Muscle</p>
                </div>
              </div>

              {/* Progress bar */}
              {f.has_baseline && (
                <div>
                  <div className="flex justify-between text-[9px] mb-1">
                    <span className="text-zinc-500">Progression vers l'objectif ({f.pct_objectif}%)</span>
                    <span className="text-[#FF6B35] font-mono font-bold">{f.progress_pct}%</span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#FF6B35] to-[#FFB800] rounded-full transition-all" style={{ width: `${f.progress_pct}%` }} />
                  </div>
                </div>
              )}

              {/* Objectif détaillé */}
              {f.objective_detail && (
                <div className="bg-[#0F0F0F] rounded-lg p-3" data-testid="objective-detail-card">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider font-bold">Objectif</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      f.objective_detail.type === 'gras' ? 'bg-[#FF6B35]/15 text-[#FF6B35]' : 'bg-[#E91E8C]/15 text-[#E91E8C]'
                    }`}>
                      {f.objective_detail.label} {f.objective_detail.type === 'gras' ? '−' : '+'}{f.objective_detail.pct}%
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-[9px] text-zinc-600 mb-0.5">Départ (J0)</p>
                      <p className="font-mono text-sm text-zinc-400">{f.objective_detail.depart}{f.objective_detail.unite}</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-zinc-600 mb-0.5">Actuel</p>
                      <p className="font-mono text-sm text-white font-bold">{f.objective_detail.actuel}{f.objective_detail.unite}</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-zinc-600 mb-0.5">Cible</p>
                      <p className="font-mono text-sm text-[#FFB800] font-bold">{f.objective_detail.cible}{f.objective_detail.unite}</p>
                    </div>
                  </div>
                  <p className={`text-center text-[10px] mt-2 font-medium ${f.objective_detail.atteint ? 'text-green-400' : 'text-zinc-400'}`}>
                    {f.objective_detail.atteint
                      ? '🎉 Objectif atteint !'
                      : `Encore ${f.objective_detail.reste}${f.objective_detail.unite} de ${f.objective_detail.type === 'gras' ? 'gras à perdre' : 'muscle à prendre'}`}
                  </p>
                </div>
              )}

              {/* Objectifs par palier — roadmap avec auto-check */}
              {f.palier_objectives?.length > 0 && (
                <div className="bg-[#0F0F0F] rounded-lg p-3" data-testid="palier-objectives-card">
                  <p className="text-[9px] text-zinc-500 uppercase tracking-wider font-bold mb-2">Objectifs par palier</p>
                  <div className="space-y-1">
                    {f.palier_objectives.map((po, i) => (
                      <div key={i} className={`px-2.5 py-2 rounded-lg text-xs ${
                        po.atteint ? 'bg-green-500/10 border border-green-500/20' :
                        po.manque ? 'bg-red-500/5 border border-red-500/15' :
                        'bg-zinc-900/60 border border-zinc-800'
                      }`} data-testid={`palier-objective-${po.label}`}>
                        <div className="flex items-center gap-2.5">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                            po.atteint ? 'bg-green-500 text-black' :
                            po.manque ? 'bg-red-500/20 text-red-400' :
                            'bg-zinc-800 text-zinc-500'
                          }`}>
                            {po.atteint ? '✓' : po.manque ? '✗' : '○'}
                          </span>
                          <span className={`font-bold w-8 text-[11px] ${po.atteint ? 'text-green-400' : 'text-white'}`}>{po.label}</span>
                          <span className="text-zinc-600 text-[10px] flex-1">{po.date}</span>
                          <span className={`font-mono font-bold ${po.atteint ? 'text-green-400' : po.manque ? 'text-red-400' : 'text-[#FFB800]'}`}>
                            {f.objective_detail?.type === 'gras' ? 'Gras ≤' : 'Muscle ≥'} {po.cible}{po.unite}
                          </span>
                        </div>
                        {po.cible_poids != null && (
                          <div className="flex items-center gap-3 mt-1 ml-[30px] text-[10px]">
                            <span className="text-zinc-500">
                              Poids estimé : <span className="font-mono text-zinc-300 font-bold">{po.cible_poids}kg</span>
                            </span>
                            {po.delta_poids != null && (
                              <span className={`font-mono font-bold ${po.delta_poids < 0 ? 'text-green-400/80' : po.delta_poids > 0 ? 'text-[#E91E8C]/80' : 'text-zinc-500'}`}>
                                ({po.delta_poids > 0 ? '+' : ''}{po.delta_poids}kg vs J0)
                              </span>
                            )}
                            {po.cible_mg_kg != null && (
                              <span className="text-zinc-500">
                                MG : <span className="font-mono text-zinc-300">{po.cible_mg_kg}kg</span>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-[9px] text-zinc-600 mt-2 text-center">
                    {f.objective_detail?.type === 'gras' ? 'Gras' : 'Muscle'} à atteindre à chaque palier — coché automatiquement dès que ta mesure y arrive
                  </p>
                </div>
              )}

              {/* J0 vs Current with deltas */}
              {f.has_baseline && (
                <div className="bg-[#0F0F0F] rounded-lg p-3">
                  <p className="text-[9px] text-zinc-500 uppercase tracking-wider font-bold mb-2">J0 → Actuel</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    {[
                      { label: 'Poids', j0: f.j0.poids, current: f.latest?.poids, delta: f.deltas?.poids, unit: 'kg', goodDown: true },
                      { label: 'Gras', j0: f.j0.mg, current: f.latest?.mg, delta: f.deltas?.mg, unit: '%', goodDown: true },
                      { label: 'Muscle', j0: f.j0.mm, current: f.latest?.mm, delta: f.deltas?.mm, unit: 'kg', goodDown: false },
                      ...(f.latest?.metabolic_age ? [{ label: 'Âge mét.', j0: null, current: f.latest.metabolic_age, delta: null, unit: ' ans', goodDown: true }] : []),
                    ].map(m => (
                      <div key={m.label}>
                        <p className="text-[9px] text-zinc-600 mb-0.5">{m.label}</p>
                        <p className="font-mono text-xs text-zinc-400">{m.j0 ?? '-'}</p>
                        <p className="text-[8px] text-zinc-600">↓</p>
                        <p className="font-mono text-sm text-white font-bold">{m.current ?? '-'}{m.unit}</p>
                        {m.delta != null && (
                          <p className={`font-mono text-[10px] font-bold ${
                            (m.goodDown ? m.delta < 0 : m.delta > 0) ? 'text-green-400' : m.delta === 0 ? 'text-zinc-500' : 'text-red-400'
                          }`}>
                            {m.delta > 0 ? '+' : ''}{m.delta}{m.unit}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Eligibility */}
              <div className="flex gap-2">
                {[
                  { key: 'recomposition', label: 'Recomp', ok: f.eligibility.recomposition },
                  { key: 'perte_gras', label: 'Gras', ok: f.eligibility.perte_gras },
                  { key: 'prise_muscle', label: 'Muscle', ok: f.eligibility.prise_muscle },
                ].map(p => (
                  <span key={p.key} className={`flex-1 text-center py-1.5 rounded-lg text-[10px] font-bold ${p.ok ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-zinc-800/50 text-zinc-600 border border-zinc-800'}`}>
                    {p.ok ? '✓' : '✗'} {p.label}
                  </span>
                ))}
              </div>

              {/* Caution status */}
              {f.caution_status && f.is_challenger && (
                <div className={`rounded-lg p-2.5 flex items-center gap-2 text-xs ${
                  f.caution_status === 'validee' ? 'bg-green-500/10 border border-green-500/30' :
                  f.caution_status === 'en_danger' ? 'bg-red-500/10 border border-red-500/30' :
                  'bg-[#FFB800]/10 border border-[#FFB800]/30'
                }`}>
                  <span>{f.caution_status === 'validee' ? '✅' : f.caution_status === 'en_danger' ? '🚨' : '⏳'}</span>
                  <div>
                    <p className={`font-bold ${
                      f.caution_status === 'validee' ? 'text-green-400' :
                      f.caution_status === 'en_danger' ? 'text-red-400' : 'text-[#FFB800]'
                    }`}>
                      {f.caution_status === 'validee' ? 'Caution securisee' :
                       f.caution_status === 'en_danger' ? `Caution en danger (-${f.caution_ecart}%)` :
                       'Challenge en cours'}
                    </p>
                    <p className="text-[10px] text-zinc-500">
                      {f.caution_status === 'en_danger' ? 'Risque de perdre 5000 XPF au prochain palier' :
                       f.caution_status === 'validee' ? 'Objectif atteint — 5000 XPF securises' : 
                       `Progression: ${f.progress_pct}% / ${f.pct_objectif}%`}
                    </p>
                  </div>
                </div>
              )}

              {/* History — stats par palier avec deltas */}
              {f.history.length > 0 && (
                <div>
                  <p className="text-[9px] text-zinc-500 uppercase tracking-wider font-bold mb-2">Stats par palier officiel</p>
                  <div className="space-y-1" data-testid="palier-stats-list">
                    <div className="flex items-center gap-2 px-2.5 text-[8px] text-zinc-600 uppercase">
                      <span className="w-10"></span>
                      <span className="w-20"></span>
                      <span className="flex-1 text-center">Poids</span>
                      <span className="flex-1 text-center">Gras</span>
                      <span className="flex-1 text-center">Muscle</span>
                    </div>
                    {f.history.filter(h => h.source !== 'intermediaire').map((h, i) => (
                      <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded text-xs bg-[#0F0F0F]">
                        <span className="font-bold w-10 text-[10px] text-[#FF6B35]">
                          {h.label}
                          {h.reporte_fields?.length > 0 && <span className="text-[7px] text-[#FFB800] block leading-tight">reporté</span>}
                        </span>
                        <span className="text-zinc-600 w-20 text-[10px]">{h.date}</span>
                        {[
                          { val: h.poids, delta: h.delta_poids, unit: 'kg', goodDown: true },
                          { val: h.mg, delta: h.delta_mg, unit: '%', goodDown: true },
                          { val: h.mm, delta: h.delta_mm, unit: 'kg', goodDown: false },
                        ].map((c, ci) => (
                          <span key={ci} className="flex-1 text-center">
                            <span className="font-mono text-white block leading-tight">{c.val ?? '-'}{c.val != null ? c.unit : ''}</span>
                            {c.delta != null && c.delta !== 0 && (
                              <span className={`font-mono text-[9px] font-bold block leading-tight ${
                                (c.goodDown ? c.delta < 0 : c.delta > 0) ? 'text-green-400' : 'text-red-400'
                              }`}>{c.delta > 0 ? '+' : ''}{c.delta}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pesées intermédiaires — à part, hors classement */}
              {f.history.some(h => h.source === 'intermediaire') && (
                <div>
                  <p className="text-[9px] text-zinc-500 uppercase tracking-wider font-bold mb-2">Pesées intermédiaires (hors classement)</p>
                  <div className="space-y-1" data-testid="intermediaires-list">
                    {f.history.filter(h => h.source === 'intermediaire').map((h, i) => (
                      <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded text-xs bg-[#FF6B35]/5 border border-[#FF6B35]/10">
                        <span className="font-bold w-10 text-[10px] text-[#FFB800]">{h.label}</span>
                        <span className="text-zinc-600 w-20 text-[10px]">{h.date}</span>
                        <span className="flex-1 text-center font-mono text-white">{h.poids ?? '-'}{h.poids != null ? 'kg' : ''}</span>
                        <span className="flex-1 text-center font-mono text-white">{h.mg ?? '-'}{h.mg != null ? '%' : ''}</span>
                        <span className="flex-1 text-center font-mono text-white">{h.mm ?? '-'}{h.mm != null ? 'kg' : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Engagement act badge */}
              <EngagementBadge userId={f?.user_id} userName={challenger.nom} />

              {/* Coach Tanatas - Conseil personnalisé */}
              <ConseilTanatas pid={f.id} isChallenger={f.is_challenger} />

              {/* Source de la mesure de reference */}
              {f.source_label && (
                <div className="flex items-center gap-2 px-3 py-2 bg-[#0F0F0F] rounded-lg">
                  <span className="text-[9px] text-zinc-500">Derniere pesee :</span>
                  <span className="text-[10px] text-[#FF6B35] font-medium">{f.source_label}</span>
                </div>
              )}

              {!f.has_baseline && (
                <div className="bg-[#FFB800]/10 border border-[#FFB800]/20 rounded-lg p-3 text-center">
                  <p className="text-xs text-[#FFB800] font-bold">En attente de pesee initiale</p>
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-[#0F0F0F] rounded-lg p-2.5"><p className="font-mono text-lg font-bold text-[#FF6B35]">{(challenger.score ?? 0).toFixed(1)}</p><p className="text-[9px] text-zinc-500">Score</p></div>
              <div className="bg-[#0F0F0F] rounded-lg p-2.5"><p className="font-mono text-lg font-bold text-white">{(challenger.score_mg ?? 0).toFixed(1)}</p><p className="text-[9px] text-zinc-500">Gras</p></div>
              <div className="bg-[#0F0F0F] rounded-lg p-2.5"><p className="font-mono text-lg font-bold text-white">{(challenger.score_mm ?? 0).toFixed(1)}</p><p className="text-[9px] text-zinc-500">Muscle</p></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Member View ───────────────────────────────────────────────────────
export function ChallengeMemberView() {
  const [myPos, setMyPos] = useState(null);
  const [posError, setPosError] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  const [bettingData, setBettingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedChallenger, setSelectedChallenger] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [posRes, lbRes, betRes] = await Promise.allSettled([
        apiClient.get('/challenge/my-position'),
        apiClient.get('/challenge/leaderboard'),
        apiClient.get('/challenge/betting-view'),
      ]);
      if (posRes.status === 'fulfilled') { setMyPos(posRes.value.data); setPosError(false); }
      else if (posRes.reason?.response?.status !== 401) { setPosError(true); }
      if (lbRes.status === 'fulfilled') setLeaderboard(lbRes.value.data);
      if (betRes.status === 'fulfilled') setBettingData(betRes.value.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return <Spinner />;

  return (
    <div className="p-4 pb-24 space-y-6" data-testid="challenge-member-view">
      {/* Section 1: Ma Position */}
      <MyPositionCard data={myPos} error={posError && !myPos} onRetry={fetchAll} onOpenFiche={() => setSelectedChallenger({ pid: myPos.pid, nom: myPos.nom, profile_photo: myPos.profile_photo, rank: myPos.rank })} />

      {/* Section 1b: Ma Caution (statut clair par palier) */}
      {myPos?.is_challenger && <MaCautionCard nom={myPos.nom} />}

      {/* Section 2: Classement (Challengers + Adherents) */}
      <Leaderboard leaderboard={leaderboard} onSelectChallenger={setSelectedChallenger} />

      {/* Section 3: Pronostic du Podium */}
      <PronosticSection bettingData={bettingData} onRefresh={fetchAll} />

      {/* Section 4: Les 3 Prix */}
      <div>
        <h2 className="text-lg font-bold text-white mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          <Medal className="w-5 h-5 text-[#FFB800] inline mr-1.5" />Les 3 Prix
        </h2>
        <PodiumTab />
      </div>

      {/* Section 5: Regles du Challenge */}
      <div>
        <h2 className="text-lg font-bold text-white mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          <BookOpen className="w-5 h-5 text-[#FF6B35] inline mr-1.5" />Regles & Formules
        </h2>
        <ReglesTab />
      </div>

      {/* Detail modal */}
      {selectedChallenger && <ChallengerDetail challenger={selectedChallenger} onClose={() => setSelectedChallenger(null)} />}
    </div>
  );
}


function ConseilTanatas({ pid, isChallenger }) {
  const [conseil, setConseil] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadConseil = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/challenge/conseil-tanatas/${pid}`);
      setConseil(data.conseil);
      setExpanded(true);
    } catch { }
    setLoading(false);
  };

  if (!isChallenger) return null;

  return (
    <div className="bg-[#1B1B1B] border border-[#FF6B35]/20 rounded-xl overflow-hidden" data-testid="conseil-tanatas">
      <button onClick={expanded ? () => setExpanded(false) : loadConseil}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FF6B35]/5 transition-colors">
        <div className="w-8 h-8 rounded-full bg-[#FF6B35]/15 flex items-center justify-center flex-shrink-0">
          <Flame className="w-4 h-4 text-[#FF6B35]" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-xs font-bold text-[#FF6B35]">Coach Namaste 🙏</p>
          <p className="text-[10px] text-zinc-500">{expanded ? 'Conseils personnalises' : 'Voir les conseils pour garder ta caution'}</p>
        </div>
        {loading ? (
          <div className="w-4 h-4 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
        ) : (
          <ChevronRight className={`w-4 h-4 text-zinc-600 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        )}
      </button>
      {expanded && conseil && (
        <div className="px-4 pb-4">
          <div className="bg-[#0F0F0F] rounded-lg p-3 border-l-2 border-[#FF6B35]">
            <p className="text-xs text-zinc-300 leading-relaxed whitespace-pre-line">{conseil}</p>
          </div>
        </div>
      )}
    </div>
  );
}
